import { type CopyCatalog } from "types/i18n/CopyCatalog";

/** Label of both Bug Detected statuses, the generation and the replay verdict. */
const BUG_DETECTED_STATUS_LABEL = { en: "Bug Detected", "zh-CN": "发现缺陷", "zh-TW": "發現缺陷" };

/** Description of both Bug Detected statuses, the generation and the replay verdict. */
const BUG_DETECTED_STATUS_DESCRIPTION = {
  en: "The test ran to completion and the app did not behave as expected — a bug in the app under test.",
  "zh-CN": "测试已运行完成，但应用未按预期运行——被测应用存在缺陷。",
  "zh-TW": "測試已執行完成，但應用程式未如預期運作——受測應用程式存在缺陷。",
};

/** Copy shared across every Muggle Test surface: the language selector, generic actions and states, test lifecycle statuses, UI primitives, route titles and app-shell messages. */
export const COMMON_COPY = {
  languageSelector: {
    triggerLabel: { en: "Change language", "zh-CN": "切换语言", "zh-TW": "切換語言" },
    currentLanguageLabel: {
      en: "Language: {{language}}",
      "zh-CN": "语言：{{language}}",
      "zh-TW": "語言：{{language}}",
    },
    menuLabel: { en: "Language", "zh-CN": "语言", "zh-TW": "語言" },
  },
  actions: {
    add: { en: "Add", "zh-CN": "添加", "zh-TW": "新增" },
    apply: { en: "Apply", "zh-CN": "应用", "zh-TW": "套用" },
    back: { en: "Back", "zh-CN": "返回", "zh-TW": "返回" },
    cancel: { en: "Cancel", "zh-CN": "取消", "zh-TW": "取消" },
    clear: { en: "Clear", "zh-CN": "清除", "zh-TW": "清除" },
    close: { en: "Close", "zh-CN": "关闭", "zh-TW": "關閉" },
    confirm: { en: "Confirm", "zh-CN": "确认", "zh-TW": "確認" },
    continue: { en: "Continue", "zh-CN": "继续", "zh-TW": "繼續" },
    copy: { en: "Copy", "zh-CN": "复制", "zh-TW": "複製" },
    copied: { en: "Copied", "zh-CN": "已复制", "zh-TW": "已複製" },
    create: { en: "Create", "zh-CN": "创建", "zh-TW": "建立" },
    delete: { en: "Delete", "zh-CN": "删除", "zh-TW": "刪除" },
    done: { en: "Done", "zh-CN": "完成", "zh-TW": "完成" },
    download: { en: "Download", "zh-CN": "下载", "zh-TW": "下載" },
    edit: { en: "Edit", "zh-CN": "编辑", "zh-TW": "編輯" },
    learnMore: { en: "Learn more", "zh-CN": "了解更多", "zh-TW": "瞭解更多" },
    next: { en: "Next", "zh-CN": "下一步", "zh-TW": "下一步" },
    ok: { en: "OK", "zh-CN": "确定", "zh-TW": "確定" },
    previous: { en: "Previous", "zh-CN": "上一步", "zh-TW": "上一步" },
    refresh: { en: "Refresh", "zh-CN": "刷新", "zh-TW": "重新整理" },
    remove: { en: "Remove", "zh-CN": "移除", "zh-TW": "移除" },
    reset: { en: "Reset", "zh-CN": "重置", "zh-TW": "重設" },
    retry: { en: "Retry", "zh-CN": "重试", "zh-TW": "重試" },
    save: { en: "Save", "zh-CN": "保存", "zh-TW": "儲存" },
    search: { en: "Search", "zh-CN": "搜索", "zh-TW": "搜尋" },
    submit: { en: "Submit", "zh-CN": "提交", "zh-TW": "送出" },
    update: { en: "Update", "zh-CN": "更新", "zh-TW": "更新" },
    upload: { en: "Upload", "zh-CN": "上传", "zh-TW": "上傳" },
    view: { en: "View", "zh-CN": "查看", "zh-TW": "檢視" },
    viewDetails: { en: "View details", "zh-CN": "查看详情", "zh-TW": "檢視詳細資料" },
  },
  states: {
    loading: { en: "Loading...", "zh-CN": "加载中…", "zh-TW": "載入中…" },
    saving: { en: "Saving...", "zh-CN": "保存中…", "zh-TW": "儲存中…" },
    noResults: { en: "No results", "zh-CN": "无结果", "zh-TW": "沒有結果" },
    error: { en: "Error", "zh-CN": "错误", "zh-TW": "錯誤" },
    success: { en: "Success", "zh-CN": "成功", "zh-TW": "成功" },
    none: { en: "None", "zh-CN": "无", "zh-TW": "無" },
    unknown: { en: "Unknown", "zh-CN": "未知", "zh-TW": "未知" },
    yes: { en: "Yes", "zh-CN": "是", "zh-TW": "是" },
    no: { en: "No", "zh-CN": "否", "zh-TW": "否" },
    notAvailable: { en: "N/A", "zh-CN": "暂无", "zh-TW": "無" },
  },
  scriptVersion: {
    current: { en: "Current", "zh-CN": "当前", "zh-TW": "目前" },
  },
  scriptRecordingStatus: {
    recorded: { en: "Recorded", "zh-CN": "已录制", "zh-TW": "已錄製" },
    recording: { en: "Recording…", "zh-CN": "录制中…", "zh-TW": "錄製中…" },
    recordingFailed: { en: "Recording failed", "zh-CN": "录制失败", "zh-TW": "錄製失敗" },
  },
  testSummaryStatus: {
    label: {
      USE_CASE_DRAFTED: { en: "User Journey Drafted", "zh-CN": "用户旅程草稿", "zh-TW": "使用者旅程草稿" },
      TEST_CASE_ACTIVE: { en: "Test Case Ready", "zh-CN": "测试用例就绪", "zh-TW": "測試案例就緒" },
      TEST_CASE_DRAFTED: { en: "Test Case Drafted", "zh-CN": "测试用例草稿", "zh-TW": "測試案例草稿" },
      TEST_CASE_DEPRECATED: { en: "Test Case Deprecated", "zh-CN": "测试用例已弃用", "zh-TW": "測試案例已棄用" },
      TEST_CASE_ARCHIVED: { en: "Test Case Archived", "zh-CN": "测试用例已归档", "zh-TW": "測試案例已封存" },
      TEST_SCRIPT_INITIATED: { en: "Script Queued", "zh-CN": "脚本排队中", "zh-TW": "腳本排隊中" },
      TEST_SCRIPT_GENERATING: { en: "Generating Script", "zh-CN": "正在生成脚本", "zh-TW": "正在產生腳本" },
      TEST_SCRIPT_GENERATION_CANCELLED: { en: "Generation Cancelled", "zh-CN": "生成已取消", "zh-TW": "產生已取消" },
      TEST_SCRIPT_GENERATING_CANCELLED: { en: "Generation Cancelled", "zh-CN": "生成已取消", "zh-TW": "產生已取消" },
      TEST_SCRIPT_GENERATION_FAILED_GOAL_NOT_ACHIEVABLE: {
        en: "Goal Not Achievable",
        "zh-CN": "目标无法达成",
        "zh-TW": "目標無法達成",
      },
      TEST_SCRIPT_GENERATION_FAILED: { en: "Generation Failed", "zh-CN": "生成失败", "zh-TW": "產生失敗" },
      TEST_SCRIPT_GENERATION_BUG_DETECTED: BUG_DETECTED_STATUS_LABEL,
      TEST_SCRIPT_ACTIVE: { en: "Script Ready", "zh-CN": "脚本就绪", "zh-TW": "腳本就緒" },
      TEST_RUN_WAITING_ON_PRIMARY: { en: "Waiting on primary spec", "zh-CN": "等待主规格", "zh-TW": "等待主要規格" },
      TEST_REPLAY_PENDING: { en: "Test Queued", "zh-CN": "测试排队中", "zh-TW": "測試排隊中" },
      TEST_REPLAY_RUNNING: { en: "Test Running", "zh-CN": "测试运行中", "zh-TW": "測試執行中" },
      TEST_REPLAY_CANCELLED: { en: "Test Cancelled", "zh-CN": "测试已取消", "zh-TW": "測試已取消" },
      TEST_RUN_CANCELLED_PRIMARY_FAILED: {
        en: "Cancelled · Primary spec failed",
        "zh-CN": "已取消 · 主规格失败",
        "zh-TW": "已取消 · 主要規格失敗",
      },
      TEST_RUN_INSUFFICIENT_BALANCE: { en: "Insufficient Balance", "zh-CN": "余额不足", "zh-TW": "餘額不足" },
      TEST_REPLAY_SUCCESS: { en: "Test Passed", "zh-CN": "测试通过", "zh-TW": "測試通過" },
      TEST_REPLAY_FAILED: { en: "Test Failed", "zh-CN": "测试失败", "zh-TW": "測試失敗" },
      TEST_REPLAY_BUG_DETECTED: BUG_DETECTED_STATUS_LABEL,
    },
    description: {
      USE_CASE_DRAFTED: {
        en: "A user journey has been defined but no test cases have been added yet.",
        "zh-CN": "已定义用户旅程，但尚未添加测试用例。",
        "zh-TW": "已定義使用者旅程，但尚未新增測試案例。",
      },
      TEST_CASE_DRAFTED: {
        en: "A test case has been created. Generate its test script to proceed.",
        "zh-CN": "已创建测试用例。生成测试脚本以继续。",
        "zh-TW": "已建立測試案例。產生測試腳本以繼續。",
      },
      TEST_CASE_DEPRECATED: {
        en: "This test case is outdated and no longer actively maintained.",
        "zh-CN": "此测试用例已过时，不再维护。",
        "zh-TW": "此測試案例已過時，不再維護。",
      },
      TEST_CASE_ARCHIVED: {
        en: "This test case has been archived and is excluded from runs.",
        "zh-CN": "此测试用例已归档，不参与运行。",
        "zh-TW": "此測試案例已封存，不納入執行。",
      },
      TEST_SCRIPT_INITIATED: {
        en: "Script generation has been requested and is waiting to start.",
        "zh-CN": "已请求生成脚本，正在等待开始。",
        "zh-TW": "已要求產生腳本，正在等待開始。",
      },
      TEST_SCRIPT_GENERATING: {
        en: "AI is actively generating the test script for this test case.",
        "zh-CN": "AI 正在为此测试用例生成测试脚本。",
        "zh-TW": "AI 正在為此測試案例產生測試腳本。",
      },
      TEST_SCRIPT_GENERATION_CANCELLED: {
        en: "Script generation was cancelled before completing.",
        "zh-CN": "脚本生成在完成前已取消。",
        "zh-TW": "腳本產生在完成前已取消。",
      },
      TEST_SCRIPT_GENERATING_CANCELLED: {
        en: "Script generation was cancelled before completing.",
        "zh-CN": "脚本生成在完成前已取消。",
        "zh-TW": "腳本產生在完成前已取消。",
      },
      TEST_SCRIPT_GENERATION_FAILED_GOAL_NOT_ACHIEVABLE: {
        en: "The AI determined this scenario cannot be automated. The goal may require human judgment or is not technically testable.",
        "zh-CN": "AI 判断此场景无法自动化。该目标可能需要人工判断，或在技术上无法测试。",
        "zh-TW": "AI 判斷此情境無法自動化。該目標可能需要人工判斷，或在技術上無法測試。",
      },
      TEST_SCRIPT_GENERATION_FAILED: {
        en: "An error occurred during script generation. Try regenerating.",
        "zh-CN": "脚本生成过程中出错。请尝试重新生成。",
        "zh-TW": "腳本產生過程中發生錯誤。請嘗試重新產生。",
      },
      TEST_SCRIPT_GENERATION_BUG_DETECTED: BUG_DETECTED_STATUS_DESCRIPTION,
      TEST_SCRIPT_ACTIVE: {
        en: "The test script has been generated successfully and is ready to replay.",
        "zh-CN": "测试脚本已成功生成，可以回放。",
        "zh-TW": "測試腳本已成功產生，可以重播。",
      },
      TEST_CASE_ACTIVE: {
        en: "This test case has a ready script and has passed at least one replay.",
        "zh-CN": "此测试用例的脚本已就绪，且至少通过了一次回放。",
        "zh-TW": "此測試案例的腳本已就緒，且至少通過一次重播。",
      },
      TEST_RUN_WAITING_ON_PRIMARY: {
        en: "This spec waits for the primary spec's run and starts once that run passes.",
        "zh-CN": "此规格正在等待主规格的运行，主规格运行通过后即开始。",
        "zh-TW": "此規格正在等待主要規格的執行，主要規格執行通過後即開始。",
      },
      TEST_REPLAY_PENDING: {
        en: "The test run is queued and will start shortly.",
        "zh-CN": "测试运行已排队，即将开始。",
        "zh-TW": "測試執行已排入佇列，即將開始。",
      },
      TEST_REPLAY_RUNNING: {
        en: "The test is running against the live environment.",
        "zh-CN": "测试正在线上环境中运行。",
        "zh-TW": "測試正在線上環境中執行。",
      },
      TEST_REPLAY_CANCELLED: {
        en: "The test run was cancelled before completing.",
        "zh-CN": "测试运行在完成前已取消。",
        "zh-TW": "測試執行在完成前已取消。",
      },
      TEST_RUN_CANCELLED_PRIMARY_FAILED: {
        en: "Cancelled before it started because the primary spec's run failed. Fix the primary spec, then run again.",
        "zh-CN": "由于主规格的运行失败，此次运行未开始即被取消。请修复主规格后再运行一次。",
        "zh-TW": "由於主要規格的執行失敗，此次執行未開始即被取消。請修正主要規格後再執行一次。",
      },
      TEST_RUN_INSUFFICIENT_BALANCE: {
        en: "The run stopped because the account ran out of balance, so nothing was tested. Top up, then run it again.",
        "zh-CN": "账户余额不足，运行已停止，未进行任何测试。请充值后再运行一次。",
        "zh-TW": "帳戶餘額不足，執行已停止，未進行任何測試。請儲值後再執行一次。",
      },
      TEST_REPLAY_FAILED: {
        en: "The test failed. Open 'Details' to see what went wrong.",
        "zh-CN": "测试失败。打开“详情”查看出错原因。",
        "zh-TW": "測試失敗。開啟「詳細資料」查看出錯原因。",
      },
      TEST_REPLAY_BUG_DETECTED: BUG_DETECTED_STATUS_DESCRIPTION,
      TEST_REPLAY_SUCCESS: {
        en: "The test passed. No issues detected.",
        "zh-CN": "测试通过，未发现问题。",
        "zh-TW": "測試通過，未發現問題。",
      },
    },
  },
  subscriptionPlan: {
    free: { en: "Free", "zh-CN": "免费版", "zh-TW": "免費版" },
    starter: { en: "Starter", "zh-CN": "入门版", "zh-TW": "入門版" },
    professional: { en: "Professional", "zh-CN": "专业版", "zh-TW": "專業版" },
    enterprise: { en: "Enterprise", "zh-CN": "企业版", "zh-TW": "企業版" },
  },
  ui: {
    select: {
      placeholder: { en: "Select", "zh-CN": "请选择", "zh-TW": "請選擇" },
      searchLabel: { en: "Search options", "zh-CN": "搜索选项", "zh-TW": "搜尋選項" },
      noMatches: { en: "No matches", "zh-CN": "无匹配项", "zh-TW": "沒有相符項目" },
    },
    multiSelect: {
      listLabel: { en: "Options", "zh-CN": "选项", "zh-TW": "選項" },
      selectedCount: {
        en: "{{count}} of {{total}} selected",
        "zh-CN": "已选 {{count}}/{{total}} 项",
        "zh-TW": "已選取 {{count}}/{{total}} 項",
      },
      panelTitle: { en: "Select options", "zh-CN": "选择选项", "zh-TW": "選擇選項" },
      searchPlaceholder: { en: "Search options...", "zh-CN": "搜索选项…", "zh-TW": "搜尋選項…" },
    },
    searchableSelect: {
      searchPlaceholder: { en: "Search...", "zh-CN": "搜索…", "zh-TW": "搜尋…" },
    },
    pagination: {
      navigationLabel: { en: "pagination", "zh-CN": "分页", "zh-TW": "分頁" },
      previousPageLabel: { en: "Go to previous page", "zh-CN": "转到上一页", "zh-TW": "前往上一頁" },
      previous: { en: "Previous", "zh-CN": "上一页", "zh-TW": "上一頁" },
      nextPageLabel: { en: "Go to next page", "zh-CN": "转到下一页", "zh-TW": "前往下一頁" },
      next: { en: "Next", "zh-CN": "下一页", "zh-TW": "下一頁" },
      morePages: { en: "More pages", "zh-CN": "更多页", "zh-TW": "更多頁面" },
    },
    breadcrumb: {
      navigationLabel: { en: "breadcrumb", "zh-CN": "面包屑导航", "zh-TW": "導覽路徑" },
      more: { en: "More", "zh-CN": "更多", "zh-TW": "更多" },
    },
    carousel: {
      previousSlide: { en: "Previous slide", "zh-CN": "上一张", "zh-TW": "上一張" },
      nextSlide: { en: "Next slide", "zh-CN": "下一张", "zh-TW": "下一張" },
    },
    sidebar: {
      toggle: { en: "Toggle Sidebar", "zh-CN": "切换侧边栏", "zh-TW": "切換側邊欄" },
    },
    twoColumnDialog: {
      title: { en: "Image Viewer", "zh-CN": "图片查看器", "zh-TW": "圖片檢視器" },
      description: { en: "View image details", "zh-CN": "查看图片详情", "zh-TW": "檢視圖片詳細資料" },
    },
  },
  themeToggle: {
    switchToLight: { en: "Switch to light mode", "zh-CN": "切换到浅色模式", "zh-TW": "切換為淺色模式" },
    switchToDark: { en: "Switch to dark mode", "zh-CN": "切换到深色模式", "zh-TW": "切換為深色模式" },
  },
  themeProviderDemo: {
    title: { en: "Theme Provider", "zh-CN": "主题提供程序", "zh-TW": "主題提供者" },
    backToDashboard: { en: "Back to Dashboard", "zh-CN": "返回控制台", "zh-TW": "返回儀表板" },
    description: {
      en: "This is the theme provider component.",
      "zh-CN": "这是主题提供程序组件。",
      "zh-TW": "這是主題提供者元件。",
    },
  },
  pageTitles: {
    signInCallback: { en: "Sign In Callback", "zh-CN": "登录回调", "zh-TW": "登入回呼" },
    home: { en: "Home", "zh-CN": "首页", "zh-TW": "首頁" },
    landing: { en: "Landing", "zh-CN": "落地页", "zh-TW": "到達網頁" },
    invite: { en: "Invite", "zh-CN": "邀请", "zh-TW": "邀請" },
    support: { en: "Support", "zh-CN": "支持", "zh-TW": "支援" },
    docs: { en: "Docs", "zh-CN": "文档", "zh-TW": "文件" },
    docsArticle: { en: "Docs Article", "zh-CN": "文档文章", "zh-TW": "文件文章" },
    termsAndPrivacy: { en: "Terms & Privacy", "zh-CN": "条款与隐私", "zh-TW": "條款與隱私權" },
    serviceStatus: { en: "Service Status", "zh-CN": "服务状态", "zh-TW": "服務狀態" },
    dashboard: { en: "Dashboard", "zh-CN": "控制台", "zh-TW": "儀表板" },
    settings: { en: "Settings", "zh-CN": "设置", "zh-TW": "設定" },
    projects: { en: "Projects", "zh-CN": "项目", "zh-TW": "專案" },
    projectDetails: { en: "Project Details", "zh-CN": "项目详情", "zh-TW": "專案詳細資料" },
    createProjectWebsite: { en: "Create Project - Website", "zh-CN": "创建项目 - 网站", "zh-TW": "建立專案 - 網站" },
    createProjectRequirements: {
      en: "Create Project - Requirements",
      "zh-CN": "创建项目 - 需求",
      "zh-TW": "建立專案 - 需求",
    },
    createProjectPrd: { en: "Create Project - PRD", "zh-CN": "创建项目 - PRD", "zh-TW": "建立專案 - PRD" },
    createProjectCredentials: {
      en: "Create Project - Credentials",
      "zh-CN": "创建项目 - 登录凭据",
      "zh-TW": "建立專案 - 登入憑證",
    },
    createProjectProgress: { en: "Create Project - Progress", "zh-CN": "创建项目 - 进度", "zh-TW": "建立專案 - 進度" },
    projectTestCases: { en: "Project Test Cases", "zh-CN": "项目测试用例", "zh-TW": "專案測試案例" },
    projectScripts: { en: "Project Scripts", "zh-CN": "项目脚本", "zh-TW": "專案腳本" },
    projectRuns: { en: "Project Runs", "zh-CN": "项目运行记录", "zh-TW": "專案執行紀錄" },
    projectReport: { en: "Project Report", "zh-CN": "项目报告", "zh-TW": "專案報告" },
    projectSchedules: { en: "Project Schedules", "zh-CN": "项目定时任务", "zh-TW": "專案排程" },
    profile: { en: "Profile", "zh-CN": "个人资料", "zh-TW": "個人資料" },
    billing: { en: "Billing", "zh-CN": "账单", "zh-TW": "帳單" },
    analytics: { en: "Analytics", "zh-CN": "数据分析", "zh-TW": "數據分析" },
    account: { en: "Account", "zh-CN": "账户", "zh-TW": "帳戶" },
    schedule: { en: "Schedule", "zh-CN": "定时任务", "zh-TW": "排程" },
    organization: { en: "Organization", "zh-CN": "组织", "zh-TW": "組織" },
    organizationMembers: { en: "Organization Members", "zh-CN": "组织成员", "zh-TW": "組織成員" },
    organizationCapacity: { en: "Organization Capacity", "zh-CN": "组织容量", "zh-TW": "組織容量" },
    organizationBilling: { en: "Organization Billing", "zh-CN": "组织账单", "zh-TW": "組織帳單" },
    organizationUsage: { en: "Organization Usage", "zh-CN": "组织用量", "zh-TW": "組織用量" },
    acceptOrganizationInvitation: {
      en: "Accept Organization Invitation",
      "zh-CN": "接受组织邀请",
      "zh-TW": "接受組織邀請",
    },
    enterpriseBilling: { en: "Enterprise Billing", "zh-CN": "企业账单", "zh-TW": "企業帳單" },
    pricingCases: { en: "Pricing Cases", "zh-CN": "定价咨询", "zh-TW": "定價諮詢" },
  },
  session: {
    expiredRedirecting: {
      en: "Session expired. Redirecting to login...",
      "zh-CN": "会话已过期，正在跳转到登录页…",
      "zh-TW": "工作階段已過期，正在重新導向至登入頁面…",
    },
    unknownInitializationError: {
      en: "Unknown error during session initialization",
      "zh-CN": "会话初始化时发生未知错误",
      "zh-TW": "工作階段初始化時發生未知錯誤",
    },
  },
  errorBoundary: {
    newVersion: {
      title: { en: "New Version Available", "zh-CN": "有新版本可用", "zh-TW": "有新版本可用" },
      description: {
        en: "A new version of the application has been deployed. Please reload the page to get the latest updates.",
        "zh-CN": "应用已部署新版本。请重新加载页面以获取最新更新。",
        "zh-TW": "應用程式已部署新版本。請重新載入頁面以取得最新更新。",
      },
      explanation: {
        en: "This happens when updates are released while you have the app open.",
        "zh-CN": "如果在你打开应用期间发布了更新，就会出现这种情况。",
        "zh-TW": "如果在你開啟應用程式期間發布了更新，就會出現這種情況。",
      },
    },
    crash: {
      title: { en: "Something went wrong", "zh-CN": "出错了", "zh-TW": "發生錯誤" },
      description: {
        en: "We're sorry, but something unexpected happened. Our team has been notified.",
        "zh-CN": "很抱歉，发生了意外错误。我们的团队已收到通知。",
        "zh-TW": "很抱歉，發生了未預期的錯誤。我們的團隊已收到通知。",
      },
      developmentDetailsHeading: {
        en: "Error Details (Development Only):",
        "zh-CN": "错误详情（仅限开发环境）：",
        "zh-TW": "錯誤詳細資料（僅限開發環境）：",
      },
    },
    reloadPage: { en: "Reload Page", "zh-CN": "重新加载页面", "zh-TW": "重新載入頁面" },
    tryAgain: { en: "Try Again", "zh-CN": "重试", "zh-TW": "再試一次" },
  },
  versionUpdate: {
    title: { en: "New version available", "zh-CN": "有新版本可用", "zh-TW": "有新版本可用" },
    description: {
      en: "Reload to pick up the latest update.",
      "zh-CN": "重新加载以获取最新更新。",
      "zh-TW": "重新載入以取得最新更新。",
    },
    reloadNow: { en: "Reload now", "zh-CN": "立即重新加载", "zh-TW": "立即重新載入" },
    reloadingTitle: { en: "Updating…", "zh-CN": "正在更新…", "zh-TW": "正在更新…" },
    reloadingDescription: {
      en: "Installing the latest version. The page will reload in a moment.",
      "zh-CN": "正在安装最新版本，页面即将重新加载。",
      "zh-TW": "正在安裝最新版本，頁面即將重新載入。",
    },
  },
  backendUnavailable: {
    eyebrow: { en: "Service notice", "zh-CN": "服务通知", "zh-TW": "服務公告" },
    title: { en: "We cannot reach our servers", "zh-CN": "无法连接到我们的服务器", "zh-TW": "無法連線至我們的伺服器" },
    description: {
      en: "Our backend is temporarily unavailable. This may be due to maintenance, a short outage, or a network disruption. Your data is safe and we are working to restore access as quickly as possible.",
      "zh-CN": "我们的后端服务暂时不可用，可能是由于维护、短暂故障或网络中断。你的数据是安全的，我们正在尽快恢复访问。",
      "zh-TW":
        "我們的後端服務暫時無法使用，可能是因為維護、短暫中斷或網路問題。你的資料安全無虞，我們正在盡快恢復存取。",
    },
    suggestionsHeading: { en: "What you can try", "zh-CN": "你可以尝试", "zh-TW": "你可以嘗試" },
    suggestions: {
      retryLater: {
        en: "Retry in a moment. Most outages resolve in under a minute once services restart.",
        "zh-CN": "稍后重试。服务重启后，大多数故障会在一分钟内恢复。",
        "zh-TW": "稍後再試。服務重新啟動後，大多數中斷會在一分鐘內恢復。",
      },
      checkStatusPage: {
        en: "Check our service status page for live availability of the API.",
        "zh-CN": "查看服务状态页，了解 API 的实时可用性。",
        "zh-TW": "查看服務狀態頁面，瞭解 API 的即時可用性。",
      },
      checkNetwork: {
        en: "Check your network connection or VPN if you are on a restricted network.",
        "zh-CN": "如果你处于受限网络中，请检查网络连接或 VPN。",
        "zh-TW": "如果你位於受限網路中，請檢查網路連線或 VPN。",
      },
      contactAdmin: {
        en: "If this persists, reach out to your workspace admin so we can investigate.",
        "zh-CN": "如果问题持续存在，请联系你的工作区管理员，以便我们进行排查。",
        "zh-TW": "如果問題持續發生，請聯絡你的工作區管理員，以便我們進行調查。",
      },
    },
    actions: {
      retryConnection: { en: "Retry connection", "zh-CN": "重新连接", "zh-TW": "重新連線" },
      viewStatusPage: { en: "View status page", "zh-CN": "查看状态页", "zh-TW": "檢視狀態頁面" },
      returnHome: { en: "Return home", "zh-CN": "返回首页", "zh-TW": "返回首頁" },
      signOut: { en: "Sign out", "zh-CN": "退出登录", "zh-TW": "登出" },
    },
    errorDetailsHeading: { en: "Error details", "zh-CN": "错误详情", "zh-TW": "錯誤詳細資料" },
    accountInfoHeading: { en: "Account information", "zh-CN": "账户信息", "zh-TW": "帳戶資訊" },
    email: { en: "Email: {{email}}", "zh-CN": "邮箱：{{email}}", "zh-TW": "電子郵件：{{email}}" },
    userId: { en: "User ID: {{userId}}", "zh-CN": "用户 ID：{{userId}}", "zh-TW": "使用者 ID：{{userId}}" },
  },
  cookieConsent: {
    acceptAll: { en: "Accept all", "zh-CN": "全部接受", "zh-TW": "全部接受" },
    rejectAll: { en: "Reject all", "zh-CN": "全部拒绝", "zh-TW": "全部拒絕" },
    consentModal: {
      title: { en: "We use cookies", "zh-CN": "我们使用 Cookie", "zh-TW": "我們使用 Cookie" },
      description: {
        en: "We use cookies to analyze site traffic and improve your experience. You can choose which categories to allow.",
        "zh-CN": "我们使用 Cookie 来分析网站流量并改善你的体验。你可以选择允许哪些类别。",
        "zh-TW": "我們使用 Cookie 分析網站流量並改善你的使用體驗。你可以選擇要允許的類別。",
      },
      managePreferences: { en: "Manage preferences", "zh-CN": "管理偏好设置", "zh-TW": "管理偏好設定" },
    },
    preferencesModal: {
      title: { en: "Cookie preferences", "zh-CN": "Cookie 偏好设置", "zh-TW": "Cookie 偏好設定" },
      savePreferences: { en: "Save preferences", "zh-CN": "保存偏好设置", "zh-TW": "儲存偏好設定" },
    },
    categories: {
      necessary: {
        title: { en: "Necessary cookies", "zh-CN": "必要 Cookie", "zh-TW": "必要 Cookie" },
        description: {
          en: "Essential cookies required for the website to function. They enable core features like security and session management.",
          "zh-CN": "网站正常运行所必需的 Cookie，用于支持安全和会话管理等核心功能。",
          "zh-TW": "網站正常運作所必需的 Cookie，用於支援安全性與工作階段管理等核心功能。",
        },
      },
      analytics: {
        title: { en: "Analytics cookies", "zh-CN": "数据分析 Cookie", "zh-TW": "數據分析 Cookie" },
        description: {
          en: "These cookies help us understand how visitors interact with our website by collecting anonymous usage data via Google Analytics.",
          "zh-CN": "这些 Cookie 通过 Google Analytics 收集匿名使用数据，帮助我们了解访问者如何与网站互动。",
          "zh-TW": "這些 Cookie 會透過 Google Analytics 收集匿名使用資料，協助我們瞭解訪客如何與網站互動。",
        },
      },
      marketing: {
        title: { en: "Marketing cookies", "zh-CN": "营销 Cookie", "zh-TW": "行銷 Cookie" },
        description: {
          en: "These cookies are used to deliver relevant advertisements and track ad campaign performance via X (Twitter) Ads.",
          "zh-CN": "这些 Cookie 用于通过 X（Twitter）Ads 投放相关广告，并跟踪广告活动的效果。",
          "zh-TW": "這些 Cookie 用於透過 X（Twitter）Ads 放送相關廣告，並追蹤廣告活動成效。",
        },
      },
    },
  },
  copyableField: {
    copiedTitle: { en: "Copied!", "zh-CN": "已复制！", "zh-TW": "已複製！" },
    copiedDescription: {
      en: "{{label}} has been copied to your clipboard.",
      "zh-CN": "{{label}} 已复制到剪贴板。",
      "zh-TW": "{{label}} 已複製到剪貼簿。",
    },
  },
  dataStateDisplay: {
    loading: { en: "Loading data...", "zh-CN": "正在加载数据…", "zh-TW": "正在載入資料…" },
    loadFailed: { en: "Failed to load data", "zh-CN": "加载数据失败", "zh-TW": "無法載入資料" },
    noData: { en: "No data available.", "zh-CN": "暂无数据。", "zh-TW": "沒有可用的資料。" },
    missingDataHint: {
      en: "Required data is missing. Please try refreshing the page.",
      "zh-CN": "缺少必要数据。请尝试刷新页面。",
      "zh-TW": "缺少必要資料。請嘗試重新整理頁面。",
    },
    refreshData: { en: "Refresh Data", "zh-CN": "刷新数据", "zh-TW": "重新整理資料" },
  },
  promptDetailsModal: {
    promptHeading: { en: "Prompt:", "zh-CN": "提示词：", "zh-TW": "提示詞：" },
    propertiesHeading: { en: "Properties:", "zh-CN": "属性：", "zh-TW": "屬性：" },
    tagsHeading: { en: "Tags:", "zh-CN": "标签：", "zh-TW": "標籤：" },
    buildWithPrompt: { en: "Build with this prompt", "zh-CN": "使用此提示词构建", "zh-TW": "使用此提示詞建構" },
  },
  paginatedList: {
    loadFailed: { en: "Failed to load list", "zh-CN": "加载列表失败", "zh-TW": "無法載入清單" },
  },
  organizationContext: {
    errors: {
      loadFailedUnknown: {
        en: "Unknown error loading organization",
        "zh-CN": "加载组织时发生未知错误",
        "zh-TW": "載入組織時發生未知的錯誤",
      },
    },
  },
} satisfies CopyCatalog;
